import { RankSupplierContractsForPurchaseMutation } from "../../../graphql/generated";
import LynksTable from "../../common/LynksTable";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableField } from "../../common/LynksTable/LynksTable";
import { capitalize } from "lodash";
import { formatCurrency } from "../../../utils/labels/formatCurrency";
import { arrayMoveImmutable } from "array-move";
import { Box, Button } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { testIds } from "./BestBuyRankingList.testIds";
import {
  BestBuySelectionMatch,
  resolveBestBuySelection,
} from "./resolveBestBuySelection";

export type BestBuyRankingListItem =
  RankSupplierContractsForPurchaseMutation["rankSupplierContractsForPurchase"][0];

export type BestBuyRankingListProps = {
  list: BestBuyRankingListItem[];
  onBestBuyItemsSelect: (items: BestBuyRankingListItem[]) => void;
  defaultSelected?: BestBuyRankingListItem;
  pinCodeOrder?: string[];
  selectedContractIds?: string[];
  selectedContractMatch?: BestBuySelectionMatch;
  loading?: boolean;
};

export default function BestBuyRankingList({
  list,
  onBestBuyItemsSelect,
  defaultSelected,
  pinCodeOrder,
  selectedContractIds,
  selectedContractMatch,
  loading,
}: BestBuyRankingListProps) {
  const { t } = useTranslation(["orders", "business", "common"]);

  const dataWithTopLevelIds = useMemo(
    () =>
      (list || []).map((el) => ({
        _id: el.supplierContract._id,
        ...el,
      })),
    [list]
  );

  const { orderedData: initialOrderedData, selectedItems: initialSelectedItems } =
    useMemo(
      () =>
        resolveBestBuySelection({
          list: dataWithTopLevelIds,
          selectedContractIds,
          pinCodeOrder,
          selectedContractMatch,
          defaultSelected: defaultSelected
            ? { ...defaultSelected, _id: defaultSelected.supplierContract._id }
            : undefined,
        }),
      [
        dataWithTopLevelIds,
        defaultSelected,
        pinCodeOrder,
        selectedContractIds,
        selectedContractMatch,
      ]
    );

  const [localData, setLocalData] = useState(initialOrderedData);

  const [localSelected, setLocalSelected] =
    useState<typeof dataWithTopLevelIds | undefined>(initialSelectedItems);

  const fields: Array<TableField<BestBuyRankingListItem>> = useMemo(
    () => [
      {
        value: (item) => item.supplierContract.contractNumber,
        label: t("orders:bestbuy.contractNumber", "Contract Number"),
        type: "string",
      },
      {
        value: (item) => {
          return String(
            localData.findIndex(
              (i) => i.supplierContract._id === item.supplierContract._id
            ) + 1
          );
        },
        label: t("orders:bestbuy.ranking", "User Rank"),
        type: "string",
      },
      {
        value: (item) => String(item.supplierContract.supplier?.name),
        label: capitalize(t("business:supplier.one", "Supplier")),
        type: "string",
      },
      {
        value: (item) => String(item.supplierContract.shipper?.name),
        label: capitalize(t("business:shipper.one", "Shipper")),
        type: "string",
      },
      {
        value: (item) => item.supplierContract.pinCode,
        label: t("orders:bestbuy.pinCode", "Pin Code"),
        type: "string",
      },
      {
        // Need backend to return this in meters instead of miles
        value: (item) => item.mileage.toFixed(2),
        label: t("orders:distance", "Distance"),
        type: "string",
      },
      {
        value: (item) => `${formatCurrency(item.productCost)}`,
        label: t("orders:bestbuy.productsCost", "Products Cost"),
        type: "string",
      },
      {
        value: (item) => `${formatCurrency(item.shippingCost)}`,
        label: t("orders:bestbuy.shippingCost", "Shipping Cost"),
        type: "string",
      },
      {
        value: (item) => `${formatCurrency(item.totalCost)}`,
        label: t("orders:bestbuy.totalCost", "Total Cost"),
        type: "string",
      },
    ],
    [t, localData]
  );

  return (
    <>
      {/* Reset button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button data-testid={testIds.bestBuyRankingListResetRanking}
          variant="outlined"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={() => {
            setLocalData(dataWithTopLevelIds);
            const reorderedSelection = dataWithTopLevelIds.filter((row) =>
              localSelected?.find((selectedRow) => selectedRow._id === row._id)
            );
            setLocalSelected(reorderedSelection);
            onBestBuyItemsSelect(reorderedSelection);
          }}
          disabled={loading}
        >
          {t("orders:bestbuy.resetRanking", "Reset Ranking")}
        </Button>
      </Box>
      <LynksTable
        fields={fields}
        id="best-buy-ranking-list"
        data={localData}
        onSelect={(records) => {
          setLocalSelected(records);
          onBestBuyItemsSelect(records);
        }}
        selected={localSelected}
        loading={loading}
        emptyState={{
          title: t("orders:bestbuy.noResults", "No contracts found"),
        }}
        rowReordering
        onRowOrderChange={(params) => {
          const reorderedRows = arrayMoveImmutable(
            localData,
            params.oldIndex,
            params.targetIndex
          );

          setLocalData(reorderedRows);

          const reorderedSelection = reorderedRows.filter((row) =>
            localSelected?.find((selectedRow) => selectedRow._id === row._id)
          );

          setLocalSelected(reorderedSelection);
          onBestBuyItemsSelect(reorderedSelection);
        }}
      />
    </>
  );
}
